import { useEffect, useRef, useState } from 'react'
import * as api from 'api/entityApi'
import type { Option } from 'types/entity'
import { nowForDateTimeInput, toLegacyDateTime } from './ContactCheckboxes'

interface FollowupFormProps {
  entityId: string
  entityType?: 'lead' | 'enquiry'
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

const REMARKS_MAX = 2000

/** Port of the legacy Add Followup action (CRMService/AddLeadFollowup_DLT). */
export function FollowupForm({ entityId, entityType = 'lead', onSuccess }: FollowupFormProps) {
  const [statuses, setStatuses] = useState<Option[]>([])
  const [users, setUsers] = useState<Option[]>([])

  const [followupStatus, setFollowupStatus] = useState<string>('')
  const [assignedUser, setAssignedUser] = useState<string>('')
  const [followupDate, setFollowupDate] = useState<string>(nowForDateTimeInput)
  const [remarks, setRemarks] = useState<string>('')
  const [amountPaid, setAmountPaid] = useState<string>('0')
  const [reAssign, setReAssign] = useState<boolean>(false)

  // Product autocomplete + chips.
  const [productFilter, setProductFilter] = useState<string>('')
  const [suggestions, setSuggestions] = useState<Option[]>([])
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false)
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])
  const debounceRef = useRef<number | undefined>(undefined)

  // Reminders (legacy IsRemindBySms / IsRemindByEmail).
  const [notifyBySms, setNotifyBySms] = useState<boolean>(false)
  const [notifyByEmail, setNotifyByEmail] = useState<boolean>(false)
  const [smsSchedule, setSmsSchedule] = useState<string>('')
  const [emailSchedule, setEmailSchedule] = useState<string>('')
  const [dltRegistered, setDltRegistered] = useState<boolean>(true)
  const [dltMessage, setDltMessage] = useState<string>('')
  const [dltTemplateId, setDltTemplateId] = useState<string>('')

  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    let active = true
    async function loadLookups() {
      try {
        const [stList, usrList, dlt] = await Promise.all([
          api.fetchFollowupStatuses(),
          api.fetchAssignedUsers(),
          api.fetchDltTemplateStatus('followup reminder').catch(() => ({
            isRegistered: false,
            dltTemplateId: '',
            message: 'DLT registration status could not be checked.',
          })),
        ])
        if (!active) return
        setStatuses(stList)
        setUsers(usrList)
        if (usrList.length > 0) setAssignedUser(usrList[0].id)
        setDltRegistered(dlt.isRegistered)
        setDltTemplateId(dlt.dltTemplateId)
        setDltMessage(dlt.message)
      } catch {
        if (active) setError('Failed to load followup metadata.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void loadLookups()
    return () => {
      active = false
    }
  }, [])

  // Debounced product autocomplete (legacy GetProductsByName, ~300ms).
  useEffect(() => {
    window.clearTimeout(debounceRef.current)
    if (!productFilter.trim()) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    debounceRef.current = window.setTimeout(() => {
      api
        .fetchProductSuggestions(productFilter.trim())
        .then((list) => {
          setSuggestions(list)
          setShowSuggestions(true)
        })
        .catch(() => setSuggestions([]))
    }, 300)
    return () => window.clearTimeout(debounceRef.current)
  }, [productFilter])

  const addProduct = (name: string) => {
    if (name && !selectedProducts.includes(name)) {
      setSelectedProducts((prev) => [...prev, name])
    }
    setProductFilter('')
    setSuggestions([])
    setShowSuggestions(false)
  }

  const removeProduct = (name: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p !== name))
  }

  const handleReset = () => {
    setFollowupStatus('')
    if (users.length > 0) setAssignedUser(users[0].id)
    setFollowupDate(nowForDateTimeInput())
    setRemarks('')
    setSelectedProducts([])
    setProductFilter('')
    setAmountPaid('0')
    setReAssign(false)
    setNotifyBySms(false)
    setNotifyByEmail(false)
    setSmsSchedule('')
    setEmailSchedule('')
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!followupStatus || followupStatus === '0') {
      setError('Please select a followup status.')
      return
    }
    if (!followupDate) {
      setError('Followup date & time is required.')
      return
    }
    if (!remarks.trim()) {
      setError('Remarks field is required.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const msg = await api.submitFollowup({
        entityId,
        entityType,
        followupStatus,
        assignedUser,
        followupDate: toLegacyDateTime(followupDate),
        remarks,
        selectedProducts,
        amountPaid: Number(amountPaid) || 0,
        reAssign,
        notifyBySms,
        notifyByEmail,
        smsScheduleDateTime: notifyBySms ? toLegacyDateTime(smsSchedule) : '',
        emailScheduleDateTime: notifyByEmail ? toLegacyDateTime(emailSchedule) : '',
        dltTemplateId,
      })
      onSuccess?.(msg)
      handleReset()
    } catch (err) {
      setError((err as Error).message || 'Failed to save followup.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="widget-loader">Loading Followup details…</div>
  }

  return (
    <div className="action-form">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="fu-status">
          Followup Status <span className="req">*</span>
        </label>
        <select
          id="fu-status"
          className="form-control"
          value={followupStatus}
          onChange={(e) => setFollowupStatus(e.target.value)}
        >
          <option value="">-- Select Status --</option>
          {statuses.map((st) => (
            <option key={st.id} value={st.id}>
              {st.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="fu-assign">
          Assign To
        </label>
        <select
          id="fu-assign"
          className="form-control"
          value={assignedUser}
          onChange={(e) => setAssignedUser(e.target.value)}
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="fu-date">
          Followup Date &amp; Time <span className="req">*</span>
        </label>
        <input
          id="fu-date"
          type="datetime-local"
          className="form-control"
          value={followupDate}
          onChange={(e) => setFollowupDate(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="fu-remarks">
          Remarks <span className="req">*</span>
        </label>
        <textarea
          id="fu-remarks"
          rows={3}
          className="form-control"
          placeholder="Remarks"
          maxLength={REMARKS_MAX}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
        <div className="form-hint">
          {remarks.length}/{REMARKS_MAX} characters
        </div>
      </div>

      <div className="form-group" style={{ position: 'relative' }}>
        <label className="form-label" htmlFor="fu-products">
          Products
        </label>
        <input
          id="fu-products"
          type="text"
          className="form-control"
          placeholder="Type to search products…"
          autoComplete="off"
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          onBlur={() => window.setTimeout(() => setShowSuggestions(false), 200)}
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul
            className="autocomplete-list"
            style={{
              position: 'absolute',
              zIndex: 5,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              listStyle: 'none',
              margin: 0,
              padding: 0,
              width: '100%',
              maxHeight: 160,
              overflowY: 'auto',
            }}
          >
            {suggestions.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="btn"
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addProduct(s.name)}
                >
                  {s.name}
                </button>
              </li>
            ))}
          </ul>
        )}
        {selectedProducts.length > 0 && (
          <div className="tag-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {selectedProducts.map((p) => (
              <span key={p} className="tag-chip">
                {p}{' '}
                <button
                  type="button"
                  aria-label={`Remove ${p}`}
                  onClick={() => removeProduct(p)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="fu-amount">
          Amount Paid
        </label>
        <input
          id="fu-amount"
          type="number"
          className="form-control"
          value={amountPaid}
          onChange={(e) => setAmountPaid(e.target.value)}
        />
      </div>

      <div className="form-group flex-row">
        <label className="checkbox-label" htmlFor="fu-reassign">
          <input
            id="fu-reassign"
            type="checkbox"
            checked={reAssign}
            onChange={(e) => setReAssign(e.target.checked)}
          />
          <span>Re Assign</span>
        </label>
      </div>

      <div className="form-group">
        <label
          className="checkbox-label"
          htmlFor="fu-remind-sms"
          title={!dltRegistered ? dltMessage : undefined}
        >
          <input
            id="fu-remind-sms"
            type="checkbox"
            disabled={!dltRegistered}
            checked={notifyBySms}
            onChange={(e) => setNotifyBySms(e.target.checked)}
          />
          <span>Remind by SMS</span>
        </label>
        {!dltRegistered && dltMessage && <div className="form-hint">{dltMessage}</div>}
        {notifyBySms && (
          <div style={{ marginTop: 6 }}>
            <label className="form-label" htmlFor="fu-sms-schedule">
              SMS Reminder Time
            </label>
            <input
              id="fu-sms-schedule"
              type="datetime-local"
              className="form-control"
              value={smsSchedule}
              onChange={(e) => setSmsSchedule(e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="checkbox-label" htmlFor="fu-remind-email">
          <input
            id="fu-remind-email"
            type="checkbox"
            checked={notifyByEmail}
            onChange={(e) => setNotifyByEmail(e.target.checked)}
          />
          <span>Remind by Email</span>
        </label>
        {notifyByEmail && (
          <div style={{ marginTop: 6 }}>
            <label className="form-label" htmlFor="fu-email-schedule">
              Email Reminder Time
            </label>
            <input
              id="fu-email-schedule"
              type="datetime-local"
              className="form-control"
              value={emailSchedule}
              onChange={(e) => setEmailSchedule(e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="action-form-footer">
        <button
          type="button"
          className="btn btn-default"
          onClick={handleReset}
          disabled={submitting}
        >
          Reset
        </button>
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
